import React from "react";
import { Bold, Italic, Underline, Strikethrough, Link as LinkIcon, Image as ImageIcon, Quote, Code, EyeOff, List, Film } from "lucide-react";

/**
 * Toolbar buttons that wrap the current selection (or insert a placeholder)
 * inside the BBCode textarea referenced by `textareaRef`. After insertion,
 * the caret is placed inside the inserted tags for natural typing.
 */
function wrapSelection(textareaRef, openTag, closeTag, valueSetter, placeholder = "text") {
  const el = textareaRef?.current;
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const before = el.value.slice(0, start);
  const sel = el.value.slice(start, end) || placeholder;
  const after = el.value.slice(end);
  const next = `${before}${openTag}${sel}${closeTag}${after}`;
  valueSetter(next);
  // Restore selection inside the wrapped block
  requestAnimationFrame(() => {
    el.focus();
    const selStart = before.length + openTag.length;
    const selEnd = selStart + sel.length;
    el.setSelectionRange(selStart, selEnd);
  });
}

const BUTTONS = [
  { id: "b", title: "Bold",          icon: Bold,          open: "[b]",  close: "[/b]"  },
  { id: "i", title: "Italic",        icon: Italic,        open: "[i]",  close: "[/i]"  },
  { id: "u", title: "Underline",     icon: Underline,     open: "[u]",  close: "[/u]"  },
  { id: "s", title: "Strikethrough", icon: Strikethrough, open: "[s]",  close: "[/s]"  },
];

export default function BBCodeToolbar({ textareaRef, value, onChange }) {
  const set = (next) => onChange?.({ target: { value: next } });
  const wrap = (openTag, closeTag, placeholder) =>
    wrapSelection(textareaRef, openTag, closeTag, set, placeholder);

  const insertUrl = () => {
    const href = window.prompt("Enter URL (https://...)", "https://");
    if (!href) return;
    wrap(`[url=${href.trim()}]`, "[/url]", "link text");
  };
  const insertImg = () => {
    const src = window.prompt("Image URL (https://...)", "https://");
    if (!src) return;
    const el = textareaRef?.current; if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const next = `${el.value.slice(0, start)}[img]${src.trim()}[/img]${el.value.slice(start)}`;
    set(next);
  };

  const insertMovie = () => {
    const q = window.prompt(
      "Embed a movie or TV show — type a title (e.g. 'The Matrix') OR paste a TMDB id (e.g. movie=603, tv=1399)",
      ""
    );
    if (!q) return;
    const t = q.trim();
    // Accept patterns like "movie=603" or "tv=1399" verbatim, else search by title
    let snippet;
    const m = t.match(/^(movie|tv|title)\s*=\s*(.+)$/i);
    if (m) {
      snippet = `[${m[1].toLowerCase()}=${m[2].trim()}][/${m[1].toLowerCase()}]`;
    } else {
      snippet = `[title]${t}[/title]`;
    }
    const el = textareaRef?.current; if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const next = `${el.value.slice(0, start)}${snippet}${el.value.slice(start)}`;
    set(next);
  };

  return (
    <div data-testid="bbcode-toolbar" className="flex items-center gap-1 flex-wrap bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded px-2 py-1 mb-2">
      {BUTTONS.map((b) => (
        <button
          key={b.id}
          type="button"
          title={b.title}
          data-testid={`bbcode-${b.id}`}
          onClick={() => wrap(b.open, b.close, "text")}
          className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-text-muted)] hover:text-white hover:bg-white/10"
        >
          <b.icon className="w-3.5 h-3.5" />
        </button>
      ))}
      <div className="w-px h-5 bg-[var(--tt-border)] mx-1" />
      <button type="button" title="Link" data-testid="bbcode-url" onClick={insertUrl}
        className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-text-muted)] hover:text-white hover:bg-white/10">
        <LinkIcon className="w-3.5 h-3.5" />
      </button>
      <button type="button" title="Image" data-testid="bbcode-img" onClick={insertImg}
        className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-text-muted)] hover:text-white hover:bg-white/10">
        <ImageIcon className="w-3.5 h-3.5" />
      </button>
      <button type="button" title="Quote" data-testid="bbcode-quote"
        onClick={() => wrap("[quote]", "[/quote]", "quoted text")}
        className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-text-muted)] hover:text-white hover:bg-white/10">
        <Quote className="w-3.5 h-3.5" />
      </button>
      <button type="button" title="Code" data-testid="bbcode-code"
        onClick={() => wrap("[code]", "[/code]", "code")}
        className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-text-muted)] hover:text-white hover:bg-white/10">
        <Code className="w-3.5 h-3.5" />
      </button>
      <button type="button" title="Spoiler" data-testid="bbcode-spoiler"
        onClick={() => wrap("[spoiler]", "[/spoiler]", "hidden")}
        className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-text-muted)] hover:text-white hover:bg-white/10">
        <EyeOff className="w-3.5 h-3.5" />
      </button>
      <button type="button" title="List item" data-testid="bbcode-list"
        onClick={() => wrap("[ul]\n[li]", "[/li]\n[/ul]", "first item")}
        className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-text-muted)] hover:text-white hover:bg-white/10">
        <List className="w-3.5 h-3.5" />
      </button>
      <div className="w-px h-5 bg-[var(--tt-border)] mx-1" />
      <button type="button" title="Embed Movie / TV Show" data-testid="bbcode-movie"
        onClick={insertMovie}
        className="w-8 h-8 flex items-center justify-center rounded text-[var(--tt-primary)] hover:text-white hover:bg-[var(--tt-primary)]/20">
        <Film className="w-3.5 h-3.5" />
      </button>
      <span className="ml-auto text-[10px] text-[var(--tt-text-faint)] font-mono-tt uppercase tracking-wider pr-1 hidden sm:block">
        BBCode
      </span>
    </div>
  );
}
